<script setup lang="ts">
  import {
    Button,
    ButtonAppearance,
    ButtonSize,
    Picture,
    PictureFormat,
    Typo,
    TypoVariant,
  } from '@travelata-libs/ui-kit';
  import { computed, onMounted } from 'vue';
  import { dataLayerTracker } from '@/entities/analytics';
  import {
    AppDownloadPartner,
    DeviceType,
    downloadApp,
    getDeviceAppStoreLabel,
    useDeviceType,
  } from '@/entities/device';
  import type { Product } from '@/entities/product';
  import type { SearchCriteria } from '@/entities/searchCriteria';
  import BannerDecor from './BannerDecor.vue';
  import { LEARN_DISCOUNT_BANNER_NAME, LEARN_DISCOUNT_BANNER_SHOW_VALUE } from '../config/appDownload';
  import { SERP_LEARN_DISCOUNT_BANNER_TEXTS } from '../config/texts';

  interface LearnDiscountBannerProps {
    product: Product;
    searchCriteria: SearchCriteria;
    cardPosition: number;
  }

  defineProps<LearnDiscountBannerProps>();

  const deviceType = useDeviceType();

  const appDeviceType = computed<DeviceType>(() => deviceType.value ?? DeviceType.Android);

  const downloadLabel = computed(
    () => `${SERP_LEARN_DISCOUNT_BANNER_TEXTS.download} ${getDeviceAppStoreLabel(appDeviceType.value)}`,
  );

  const download = (): void => {
    downloadApp({
      bannerName: LEARN_DISCOUNT_BANNER_NAME,
      deviceType: appDeviceType.value,
      partner: AppDownloadPartner.MobileWeb,
    });
  };

  onMounted(() => {
    dataLayerTracker.bannerShow(LEARN_DISCOUNT_BANNER_SHOW_VALUE);
  });
</script>

<template>
  <section
    class="learn-discount-banner"
    data-testid="learn-discount-banner"
  >
    <Picture
      class="learn-discount-banner__picture"
      path="illustrations/coupon.webp"
      :extensions="[PictureFormat.WEBP]"
      :high-resolution="true"
      fit="contain"
      aria-hidden="true"
      data-testid="learn-discount-banner-picture"
    />

    <div class="learn-discount-banner__card">
      <BannerDecor variant="learn-discount" />
      <span
        class="learn-discount-banner__glow"
        aria-hidden="true"
      />

      <div class="learn-discount-banner__content">
        <div class="learn-discount-banner__text">
          <Typo
            class="learn-discount-banner__title"
            :variant="TypoVariant.Heading5"
            tag="p"
          >
            {{ SERP_LEARN_DISCOUNT_BANNER_TEXTS.title }}
          </Typo>
          <Typo
            class="learn-discount-banner__subtitle"
            :variant="TypoVariant.BodyMRegular"
            tag="p"
          >
            {{ SERP_LEARN_DISCOUNT_BANNER_TEXTS.subtitle }}
          </Typo>
        </div>

        <Button
          class="learn-discount-banner__download"
          :appearance="ButtonAppearance.Tertiary"
          :size="ButtonSize.M"
          data-testid="learn-discount-banner-download"
          @click="download"
        >
          {{ downloadLabel }}
        </Button>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  $picture-width: 262px;
  $picture-height: 195px;
  $picture-top: -10px;
  $card-top: 85px;
  $picture-space: 61px;

  .learn-discount-banner {
    align-items: center;
    display: flex;
    flex-direction: column;
    isolation: isolate;

    &__picture {
      height: $picture-height;
      margin-top: $picture-top;
      pointer-events: none;
      position: relative;
      width: $picture-width;
      z-index: 1;
    }

    &__card {
      align-self: stretch;
      background: linear-gradient(240deg, var(--color-purple-50) 0%, var(--color-purple-40) 100%);
      border-radius: var(--rounding-l);
      box-sizing: border-box;
      color: var(--color-text-inverse);
      margin-top: $card-top - $picture-top - $picture-height;
      overflow: hidden;
      padding: calc(#{$picture-space} + var(--spacing-m) * 2) var(--spacing-m) var(--spacing-m);
      position: relative;
    }

    &__glow {
      background: rgb(248 253 255 / 0.8);
      border-radius: 50%;
      filter: blur(57.2px);
      height: 60px;
      left: 10px;
      opacity: 0.18;
      pointer-events: none;
      position: absolute;
      right: var(--spacing-m);
      top: 28px;
    }

    &__content {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      position: relative;
      text-align: center;
    }

    &__text {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }

    &__title {
      max-width: 287px;
    }

    &__subtitle {
      max-width: 250px;
    }

    &__download {
      border: none;
    }
  }
</style>
